import React, { ReactNode, useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getUserFullName, isNdaNotSent } from 'utils';
import { DATE_FORMAT_WITH_FULL_MONTH, getFormattedDate } from 'dateUtils';
import usePastTime from 'hooks/usePastTime';
import { Button, Loader } from 'ncoded-component-library';
import { NdaStatus, NDA_STATUSES } from 'models/Nda';
import ListingContext from '../../providers/Listing/Listing.context';
import LeadsContext from '../../providers/Leads/Leads.context';
import LockclosedIcon from 'icons/Lockclosed.icon';
import LinkToIcon from 'icons/LinkTo.icon';
import NdaStatusBadge from 'router/subrouters/Dash/subrouters/private/subrouters/Messaging/components/NdaSigningProgress/NdaStatusBadge';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { Lead } from 'models/Lead';
import SettingsIcon from 'icons/Settings.icon';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { CollapsibleSection } from 'components/CollapsibleSection';
import AddNdaButton from 'components/AddNdaButton';
import useNdaStatus, { UpdateLeadNdaProps } from 'hooks/useNdaStatus';
import OverrideNdaStatusModal from './components/OverrideNdaStatusModal';
import NdaStatusProofFiles from './components/NdaStatusProofFiles';
import { CONTACT_US_ROUTE } from 'data/global';
import AutomaticMessageModal from 'components/AutomaticMessageModal';
import TransWithNda from 'components/TransWithNda';
import { Q_AND_A_SIDES } from 'router/subrouters/DataRoom/models/QAndA';
import { getHasNdaToSend } from 'agreementUtils';

import './NDAStatusCard.styles.scss';

type NDAStatusCardProps = {
  className?: string;
};

type ContentProps = {
  text: ReactNode;
  description?: string;
  isLoading?: boolean;
  buttonText?: string;
  isSuccess?: boolean;
  lead: Lead;
  isDisabled?: boolean;
  dataRoomId: string;
  dataRoomName: string;
  ownerId: string;
  onClick?: () => void;
};

const ContentGenerate: React.FC<ContentProps> = ({
  text,
  description,
  isLoading,
  buttonText,
  lead,
  isSuccess,
  isDisabled,
  dataRoomId,
  dataRoomName,
  ownerId,
  onClick,
}) => {
  const { t } = useTranslation();

  const [modal, modalRef] = useCallbackRef<OverlayRef>(null);

  return (
    <div className="data">
      <p className={classNames({ success: isSuccess })}>{text}</p>
      {description && <p>{description}</p>}

      <NdaStatusProofFiles
        lead={lead}
        dataRoomId={dataRoomId}
        dataRoomName={dataRoomName}
        ownerId={ownerId}
        userSide={Q_AND_A_SIDES.SELL}
      />

      <div className="data__row">
        {onClick ? (
          <Button
            className="cta"
            icon={LinkToIcon}
            variant="outline"
            disabled={isLoading || isDisabled}
            {...(!isDisabled && { onClick })}
          >
            {buttonText}
            {isLoading && <Loader size="small" />}
          </Button>
        ) : (
          <span />
        )}

        <Button
          className="override"
          variant="link"
          icon={SettingsIcon}
          disabled={isLoading}
          onClick={() => modal.open()}
        >
          {t('overrideStatus')}
          {isLoading && <Loader size="small" />}
        </Button>
        <OverrideNdaStatusModal ref={modalRef} lead={lead} />
      </div>
    </div>
  );
};

const NDAStatusCard: React.FC<NDAStatusCardProps> = (props) => {
  const { className } = props;

  const { isSuperAdmin } = useContext(CurrentUserContext);

  const {
    listing,
    listing: { _id: listingId },
    dealRoom: { name, dataRoomId, userId },
    areActionsDisabled,
    canManageDeal,
    setListing,
  } = useContext(ListingContext);
  const { selectedLead, updateLead, setSelectedLead } =
    useContext(LeadsContext);
  const {
    _id: leadId,
    firstName,
    envelopeId,
    signingStatus,
    signedDate,
    sentDate,
    rejectionReason,
    manuallySetNDAStatus,
    manuallySetNDAStatusUser,
    manuallySetNDAStatusDate,
    withdrawnInterest,
    envelopeTemplateId,
    envelopeTemplateName,
    ndaProvider,
    agreementExecutionId,
    agreementExecuted,
  } = selectedLead;

  const hasNdaToSend = getHasNdaToSend(envelopeTemplateId, listing);

  const { t } = useTranslation();

  const navigate = useNavigate();

  const classes = classNames('nda-status-card', 'white-panel', className);

  const [modal, modalRef] = useCallbackRef<OverlayRef>(null);
  const [autoMessageModal, autoMessageModalRef] =
    useCallbackRef<OverlayRef>(null);

  const updateLeadNdaProps = useCallback(
    (leadNdaProps: UpdateLeadNdaProps) => {
      const updatedLead: Lead = {
        ...selectedLead,
        ...leadNdaProps,
      };

      updateLead(updatedLead);
      setSelectedLead(updatedLead);
    },
    [selectedLead, setSelectedLead, updateLead],
  );

  const { isLoading, sendNda, resendNda, getAndOpenNdaLink } = useNdaStatus(
    leadId,
    envelopeId,
    updateLeadNdaProps,
    { ndaProvider, agreementExecutionId, agreementExecuted },
  );

  const pastTime = usePastTime();

  const TRIGGER: Record<NdaStatus, JSX.Element> = useMemo(() => {
    return {
      [NDA_STATUSES.NOT_SENT]: (
        <p className="heading">
          <LockclosedIcon />
          {t('NDAstatus')}
        </p>
      ),
      [NDA_STATUSES.SENT]: (
        <p className="heading">
          <LockclosedIcon />
          {t('NDAstatus')}
          <NdaStatusBadge
            envelopeTemplateName={envelopeTemplateName}
            status={NDA_STATUSES.SENT}
            sentDate={sentDate}
          />
        </p>
      ),
      [NDA_STATUSES.SIGNED]: (
        <p className="heading">
          <LockclosedIcon />
          {t('NDAstatus')}
          <NdaStatusBadge
            envelopeTemplateName={envelopeTemplateName}
            status={NDA_STATUSES.SIGNED}
            signedDate={signedDate}
          />
        </p>
      ),
      [NDA_STATUSES.REJECTED]: (
        <p className="heading">
          <LockclosedIcon />
          {t('NDAstatus')}
          <NdaStatusBadge
            envelopeTemplateName={envelopeTemplateName}
            status={NDA_STATUSES.REJECTED}
            rejectionReason={rejectionReason}
          />
        </p>
      ),
      [NDA_STATUSES.COUNTER_SIGNATURE_PENDING]: (
        <p className="heading">
          <LockclosedIcon />
          {t('NDAstatus')}
          <NdaStatusBadge
            envelopeTemplateName={envelopeTemplateName}
            status={NDA_STATUSES.COUNTER_SIGNATURE_PENDING}
            signedDate={signedDate}
          />
        </p>
      ),
    };
  }, [sentDate, signedDate, rejectionReason, envelopeTemplateName, t]);

  const CONTENT: Record<NdaStatus, JSX.Element> = useMemo(() => {
    return {
      [NDA_STATUSES.NOT_SENT]: (
        <>
          <ContentGenerate
            text={
              <TransWithNda
                i18nKey="NDAnotSent"
                envelopeTemplateName={envelopeTemplateName}
              />
            }
            isLoading={isLoading}
            buttonText={t('sendCA')}
            lead={selectedLead}
            isDisabled={areActionsDisabled}
            onClick={
              hasNdaToSend && canManageDeal ? autoMessageModal?.open : undefined
            }
            dataRoomName={name}
            dataRoomId={dataRoomId}
            ownerId={userId}
          />

          <AutomaticMessageModal
            ref={autoMessageModalRef}
            leadFirstName={selectedLead?.firstName}
            leadLastName={selectedLead?.lastName}
            envelopeTemplateName={envelopeTemplateName}
            onSendNda={(message) => {
              autoMessageModal?.close();
              sendNda(message);
            }}
          />
        </>
      ),
      [NDA_STATUSES.SENT]: (
        <ContentGenerate
          text={
            <TransWithNda
              i18nKey="NDAsent"
              envelopeTemplateName={envelopeTemplateName}
              values={{
                firstName,
                date: sentDate ? pastTime(sentDate) : null,
              }}
            />
          }
          isLoading={isLoading}
          lead={selectedLead}
          buttonText={t('sendAReminder')}
          isDisabled={areActionsDisabled}
          onClick={
            hasNdaToSend && canManageDeal ? () => resendNda() : undefined
          }
          dataRoomName={name}
          dataRoomId={dataRoomId}
          ownerId={userId}
        />
      ),
      [NDA_STATUSES.SIGNED]: (
        <ContentGenerate
          text={
            <TransWithNda
              i18nKey={
                manuallySetNDAStatus
                  ? manuallySetNDAStatusDate
                    ? 'manuallyUpdatedNdaWithDate'
                    : 'manuallyUpdatedNda'
                  : signedDate
                  ? 'NDAsignedWithDate'
                  : 'NDAsigned'
              }
              envelopeTemplateName={envelopeTemplateName}
              values={{
                firstName,
                date: getFormattedDate(
                  manuallySetNDAStatusDate || signedDate,
                  DATE_FORMAT_WITH_FULL_MONTH,
                ),
              }}
            />
          }
          {...(manuallySetNDAStatus &&
            manuallySetNDAStatusUser && {
              description: getUserFullName(
                manuallySetNDAStatusUser.firstName,
                manuallySetNDAStatusUser.lastName,
              ),
            })}
          isSuccess
          isLoading={isLoading}
          isDisabled={areActionsDisabled}
          onClick={!manuallySetNDAStatus ? getAndOpenNdaLink : undefined}
          buttonText={t('viewSignedNDA')}
          lead={selectedLead}
          dataRoomName={name}
          dataRoomId={dataRoomId}
          ownerId={userId}
        />
      ),
      [NDA_STATUSES.REJECTED]: (
        <ContentGenerate
          text={
            <TransWithNda
              i18nKey="userRejectedNDA"
              envelopeTemplateName={envelopeTemplateName}
              values={{
                name: firstName,
                rejectionReason: rejectionReason || '/',
              }}
            />
          }
          dataRoomName={name}
          dataRoomId={dataRoomId}
          isLoading={isLoading}
          lead={selectedLead}
          ownerId={userId}
        />
      ),
      [NDA_STATUSES.COUNTER_SIGNATURE_PENDING]: (
        <ContentGenerate
          text={
            <TransWithNda
              i18nKey="NDAcounterSignaturePending"
              envelopeTemplateName={envelopeTemplateName}
              values={{
                firstName,
              }}
            />
          }
          dataRoomName={name}
          dataRoomId={dataRoomId}
          isLoading={isLoading}
          lead={selectedLead}
          ownerId={userId}
        />
      ),
    };
  }, [
    isLoading,
    firstName,
    selectedLead,
    areActionsDisabled,
    autoMessageModal,
    sentDate,
    signedDate,
    manuallySetNDAStatus,
    manuallySetNDAStatusDate,
    manuallySetNDAStatusUser,
    hasNdaToSend,
    canManageDeal,
    rejectionReason,
    name,
    dataRoomId,
    envelopeTemplateName,
    userId,
    t,
    autoMessageModalRef,
    pastTime,
    sendNda,
    resendNda,
    getAndOpenNdaLink,
  ]);

  if (!isNdaNotSent(signingStatus)) {
    return (
      <CollapsibleSection
        defaultOpen
        className={classes}
        trigger={TRIGGER[signingStatus]}
      >
        {CONTENT[signingStatus]}
      </CollapsibleSection>
    );
  }

  if (!hasNdaToSend || withdrawnInterest)
    return (
      <CollapsibleSection
        defaultOpen
        className={classes}
        trigger={
          <p className="heading">
            <LockclosedIcon />
            {t('NDAstatus')}
            <div
              className={classNames(
                'nda-status-badge',
                'nda-status-card__no-nda',
              )}
            >
              {t('noNda')}
            </div>
          </p>
        }
      >
        <div className="data">
          <p>{t('noNdaForDeal')}</p>

          <NdaStatusProofFiles
            lead={selectedLead}
            dataRoomId={dataRoomId}
            dataRoomName={name}
            ownerId={userId}
            userSide={Q_AND_A_SIDES.SELL}
          />

          <div className="data__row">
            {isSuperAdmin ? (
              <AddNdaButton
                listingId={listingId}
                envelopeTemplateId={envelopeTemplateId}
                isDisabled={areActionsDisabled}
                setListing={setListing}
              />
            ) : (
              <Button
                variant="outline"
                onClick={() => navigate(CONTACT_US_ROUTE)}
              >
                {t('contactResolve')}
              </Button>
            )}
            <Button
              className="override"
              variant="link"
              icon={SettingsIcon}
              disabled={isLoading || areActionsDisabled}
              onClick={() => modal.open()}
            >
              {t('overrideStatus')}
              {isLoading && <Loader size="small" />}
            </Button>
            <OverrideNdaStatusModal ref={modalRef} lead={selectedLead} />
          </div>
        </div>
      </CollapsibleSection>
    );

  return (
    <CollapsibleSection
      defaultOpen
      className={classes}
      trigger={TRIGGER[signingStatus]}
    >
      {CONTENT[signingStatus]}
    </CollapsibleSection>
  );
};

export default NDAStatusCard;
